import { ChangeDetectionStrategy, Component, computed, signal } from "@angular/core";
import { debounce, form, FormField, maxLength, required } from "@angular/forms/signals";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import type { ThreadConnectionListAutoCompleteData } from "@jtms/default-connector";
import { injectThreadConnectionListAutoComplete } from "@jtms/default-connector/angular";

export interface AddConnectionFormModel {
  connection: string;
  revision: string;
  /** Which end of the thread this connection is. Drives the pin-to-box-only mate rule. */
  end: "PIN" | "BOX" | "";
}

@Component({
  selector: "lib-add-connection-form",
  imports: [FormField, MatFormFieldModule, MatInputModule, MatIconModule, MatRadioModule],
  templateUrl: "./add-connection-form.component.html",
  styleUrl: "./add-connection-form.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddConnectionForm {
  #rootModel = signal<AddConnectionFormModel>({ connection: "", revision: "", end: "" });

  rootForm = form(this.#rootModel, (schemaPath) => {
    required(schemaPath.connection, { message: "Connection name is required" });
    required(schemaPath.revision, { message: "Revision is required" });
    required(schemaPath.end, { message: "Choose pin or box" });
    // Matches the `revision varchar(5)` column in dataconnect/schema/thread_connection.gql.
    maxLength(schemaPath.revision, 5, { message: "Revision must be 5 characters or fewer" });
    // Hold rapid keystrokes back so the duplicate-name search below only re-queries once the user pauses.
    debounce(schemaPath.connection, 400);
  });

  #connectionSearchTerm = computed(() => this.rootForm.connection().value().trim());

  // Non-blocking duplicate/similar-name check: this intentionally does NOT use validateAsync(),
  // since that would make the field invalid() and block submission. The task only calls for a warning.
  #similarConnections = injectThreadConnectionListAutoComplete(
    () => ({ filterString: this.#connectionSearchTerm() }),
    () => ({ enabled: this.#connectionSearchTerm().length > 1 })
  );

  similarConnectionNames = computed(() => {
    const term = this.#connectionSearchTerm().toLowerCase();
    const matches: ThreadConnectionListAutoCompleteData["threadConnections"] =
      this.#similarConnections.data()?.threadConnections ?? [];
    return matches
      .map((match) => match.connection)
      .filter((name): name is string => !!name && name.toLowerCase() !== term);
  });

  isCheckingSimilarConnections = computed(() => this.#similarConnections.isPending());
}
